import React, { FC, useCallback, useRef } from 'react';

import { Title } from 'components/Typography';
import Button from 'components/Button/Button';
import { TButtonTypes } from 'components/Button/Button.types';
import { Colors } from 'core/CssVariables';
import { TTableProps } from './Table.types';
import { SSelectedRowWrapper, STable } from './Table.style';
import TableExpandIcons from './components/TableExpandIcons';
import TableSettings from './components/TableSettings';
import useTableSettings from './useTableSettings';
import TableTotals from './components/TableTotals';
import DraggableHeaderCell from './components/DraggableHeaderCell';

const Table: FC<TTableProps> = props => {
  const {
    pageSize,
    setPageSize,
    totalDataCount,
    selectHasOptions,
    rowSelection,
    isSelectAll,
    setIsSelectAll,
    currentPage,
    expandable,
    columns,
    tableName,
    hideSettings,
    unHidableColumnTitles,
    totals,
    ...rest
  } = props;

  const settingsInitialData = (columns || []).map(el => {
    return {
      title: el.title as string,
      show: true,
    };
  });
  const [tableSettings, setTableSettings] = useTableSettings({
    initialData: settingsInitialData,
    tableName,
  });

  const dragStartIdx = useRef(-1);
  const dragEnterIdx = useRef(-1);

  const handleDragStart = useCallback((index: number) => {
    dragStartIdx.current = index;
  }, []);

  const handleDragEnter = useCallback((index: number) => {
    dragEnterIdx.current = index;
  }, []);

  const handleDragEnd = useCallback(() => {
    const from = dragStartIdx.current;
    const to = dragEnterIdx.current;
    dragStartIdx.current = -1;
    dragEnterIdx.current = -1;
    if (from < 0 || to < 0 || from === to) return;

    setTableSettings(prev => {
      const visible = prev.filter(s => s.show);
      const hidden = prev.filter(s => !s.show);
      if (from >= visible.length || to >= visible.length) return prev;
      const [moved] = visible.splice(from, 1);
      visible.splice(to, 0, moved);
      return [...visible, ...hidden];
    });
  }, [setTableSettings]);

  const settingColumn = {
    title: (
      <TableSettings
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        unHidableColumnTitles={unHidableColumnTitles}
      />
    ),
    width: 10,
    className: 'no-drag',
  };

  // Build columns in settings order (array position = display order)
  const filteredColumns = tableSettings
    .filter(s => s.show)
    .reduce<any[]>((acc, s) => {
      const col = (columns || []).find(c => (c.title as string) === s.title);
      if (col) {
        acc.push({
          ...col,
          onHeaderCell: () => ({
            onColumnDragStart: handleDragStart,
            onColumnDragEnter: handleDragEnter,
            onColumnDragEnd: handleDragEnd,
          }),
        });
      }
      return acc;
    }, []);

  return (
    <>
      {totalDataCount > 0 &&
        !!pageSize &&
        pageSize === rowSelection?.selectedRowKeys?.length && (
          <SSelectedRowWrapper>
            <Title fontlevel={5} color={Colors.SecondaryColor} fontWeight={400}>
              All {isSelectAll ? totalDataCount : pageSize} items{' '}
              {!isSelectAll ? 'on this page ' : ''}
              are selected.
            </Title>
            <Button
              type="link"
              color={TButtonTypes.Orange}
              className="ml-1"
              {...(setIsSelectAll && {
                onClick: () => {
                  setIsSelectAll(p => !p);
                  if (rowSelection.onChange && isSelectAll) {
                    rowSelection.onChange([], []);
                  }
                },
              })}
            >
              {isSelectAll
                ? 'Clear all'
                : `Select all ${totalDataCount} items.`}
            </Button>
          </SSelectedRowWrapper>
        )}
      <STable
        locale={{
          filterConfirm: 'Filter',
        }}
        components={{
          header: { cell: DraggableHeaderCell },
        }}
        selectHasOptions={selectHasOptions}
        rowKey="id"
        tableLayout="auto"
        showSorterTooltip={false}
        {...(rowSelection && { rowSelection })}
        pagination={
          pageSize && setPageSize
            ? {
                position: ['bottomRight'],
                showSizeChanger: true,
                showTotal: (total, range) =>
                  `Results: ${range[0]}-${range[1]} of ${total}`,
                onShowSizeChange: (currentSize, size) => setPageSize(size),
                pageSize,
                total: totalDataCount,
                ...(currentPage && { current: currentPage + 1 }),
              }
            : false
        }
        expandable={{
          ...expandable,
          ...(expandable && {
            expandIcon: ({ expanded, onExpand, record }) => {
              return (
                <TableExpandIcons
                  expanded={expanded}
                  onExpand={onExpand}
                  record={record}
                  rowExpandable={expandable.rowExpandable}
                />
              );
            },
          }),
        }}
        columns={hideSettings ? columns : [...filteredColumns, settingColumn]}
        {...(totals && {
          summary: () => (
            <TableTotals totals={totals} columns={filteredColumns} />
          ),
        })}
        {...rest}
      />
    </>
  );
};

export default Table;
